import React, { useState, useEffect, useMemo, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';

function ItemStockMovement() {
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();

    const [dataItem,    setDataItem]    = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch,  setShowSearch]  = useState(false);
    const [loading,     setLoading]     = useState(false);

    // Filter state
    const [fromDate, setFromDate] = useState('');
    const [toDate,   setToDate]   = useState('');
    const [itemId,   setItemId]   = useState(0);   // 0 = all items

    // ── Item autocomplete state ──────────────────────────────────
    const [itemList,     setItemList]     = useState([]);
    const [itemSearch,   setItemSearch]   = useState('');
    const [itemDropOpen, setItemDropOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const itemDropRef = useRef(null);

    const hasReadReportPermission = permissions.includes('Read Reports');

    // ── Fetch item list for autocomplete ─────────────────────────
    useEffect(() => {
        if (!isAuthenticated || !token || !companyId) return;
        fetchData('ItemApi', `/${companyId}`, token)
            .then(res => {
                if (res.responseCode === '1111') setItemList(res.response_data);
            })
            .catch(err => console.error('Error fetching items for autocomplete:', err));
    }, [companyId, token, isAuthenticated]);

    // ── Close dropdown when clicking outside ────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (itemDropRef.current && !itemDropRef.current.contains(e.target)) {
                setItemDropOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Filtered suggestions ─────────────────────────────────────
    const itemSuggestions = useMemo(() => {
        if (!itemSearch.trim()) return itemList.slice(0, 50);
        const q = itemSearch.toLowerCase();
        return itemList
            .filter(it =>
                it.Code?.toLowerCase().includes(q) ||
                it.Name?.toLowerCase().includes(q)
            )
            .slice(0, 50);
    }, [itemList, itemSearch]);

    // ── Handlers for item autocomplete ───────────────────────────
    const handleItemInputChange = (e) => {
        setItemSearch(e.target.value);
        setItemDropOpen(true);
        if (!e.target.value) {
            setSelectedItem(null);
            setItemId(0);
        }
    };

    const handleItemSelect = (item) => {
        setSelectedItem(item);
        setItemId(item.Id);
        setItemSearch(`${item.Code} — ${item.Name}`);
        setItemDropOpen(false);
    };

    const handleItemClear = () => {
        setSelectedItem(null);
        setItemId(0);
        setItemSearch('');
        setItemDropOpen(false);
    };

    // ── Default dates (1st of current month → today) ────────────
    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate   = () => {
        const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
    };

    // ── Initial load ─────────────────────────────────────────────
    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, companyId, branchId, token, logout]);

    // ── Fetch report data ────────────────────────────────────────
    const fetchTableData = async () => {
        if (!hasReadReportPermission) return;
        if (!fromDate || !toDate) return;

        setDataItem([]);
        setLoading(true);
        try {
            const response = await fetchData(
                'ReportApi',
                `/ItemStockMovement/${fromDate}/${toDate}/${companyId}/${branchId}/${itemId}`,
                token
            );
            if (response.responseCode === '1111') {
                setDataItem(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Item Stock Movement:', error);
        } finally {
            setLoading(false);
        }
    };

    // ── Client-side search filter ────────────────────────────────
    const filteredDataItem = useMemo(() =>
        dataItem.filter(item =>
            Object.values(item).some(val =>
                String(val).toLowerCase().includes(searchQuery.toLowerCase())
            )
        ),
    [dataItem, searchQuery]);

    // ── Aggregate totals ─────────────────────────────────────────
    const totals = useMemo(() =>
        filteredDataItem.reduce((acc, r) => ({
            OpeningQty:  acc.OpeningQty  + (r.OpeningQty  || 0),
            InQty:       acc.InQty       + (r.InQty       || 0),
            OutQty:      acc.OutQty      + (r.OutQty      || 0),
            MovementQty: acc.MovementQty + (r.MovementQty || 0),
            BalanceQty:  acc.BalanceQty  + (r.BalanceQty  || 0),
            Amount:      acc.Amount      + (r.Amount      || 0),
            GSTAmount:   acc.GSTAmount   + (r.GSTAmount   || 0),
            TotalAmount: acc.TotalAmount + (r.TotalAmount || 0),
        }), {
            OpeningQty: 0, InQty: 0, OutQty: 0, MovementQty: 0,
            BalanceQty: 0, Amount: 0, GSTAmount: 0, TotalAmount: 0,
        }),
    [filteredDataItem]);

    // ── Format helpers ───────────────────────────────────────────
    const fmt2 = (v) => (v == null ? '0.00' : Number(v).toFixed(2));
    const fmt3 = (v) => (v == null ? '0.000' : Number(v).toFixed(3));
    const formatDate = (ds) => {
        if (!ds) return '';
        return new Date(ds).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    // ── Excel Export ─────────────────────────────────────────────
    const handleExportExcel = async () => {
        const workbook  = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Item Stock Movement');

        const numericCols = [
            'Opening Qty', 'In Qty', 'Out Qty', 'Movement Qty', 'Balance Qty',
            'Rate', 'Amount', 'GST Amount', 'Total Amount',
            'GST Rate', 'Current Purchase Rate', 'Current MRP'
        ];

        // Title row
        worksheet.mergeCells('A1:T1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = `Item Stock Movement  From : ${formatDate(fromDate)}  To : ${formatDate(toDate)}` +
            (selectedItem ? `  Item : ${selectedItem.Code} — ${selectedItem.Name}` : '');
        titleCell.font      = { bold: true, size: 13 };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };

        // Column headers
        const headerRowValues = [
            'Date', 'Transaction Type', 'Document No', 'Reference No',
            'Item Code', 'Item Name', 'HSN Code', 'GST Rate',
            'Opening Qty', 'In Qty', 'Out Qty', 'Movement Qty', 'Balance Qty',
            'Rate', 'Amount', 'GST Amount', 'Total Amount',
            'Remarks', 'Current Purchase Rate', 'Current MRP'
        ];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell(cell => {
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font      = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border    = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        worksheet.columns = headerRowValues.map(() => ({ width: 16 }));

        // Data rows
        filteredDataItem.forEach(r => {
            const dataRow = [
                formatDate(r.TransactionDate), r.TransactionType, r.DocumentNo, r.ReferenceNo,
                r.ItemCode, r.ItemName, r.HSNCode, parseFloat(r.GSTRate),
                parseFloat(r.OpeningQty), parseFloat(r.InQty), parseFloat(r.OutQty),
                parseFloat(r.MovementQty), parseFloat(r.BalanceQty),
                parseFloat(r.Rate), parseFloat(r.Amount), parseFloat(r.GSTAmount), parseFloat(r.TotalAmount),
                r.Remarks, parseFloat(r.CurrentPurchaseRate), parseFloat(r.CurrentMRP)
            ];
            const row = worksheet.addRow(dataRow);
            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: numericCols.includes(headerName) ? 'right' : 'center',
                };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        // Totals row
        const totalRowValues = [
            '', 'Total', '', '',
            '', '', '', '',
            totals.OpeningQty, totals.InQty, totals.OutQty, totals.MovementQty, totals.BalanceQty,
            '', parseFloat(totals.Amount.toFixed(2)),
            parseFloat(totals.GSTAmount.toFixed(2)), parseFloat(totals.TotalAmount.toFixed(2)),
            '', '', ''
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            const headerName = headerRowValues[colNumber - 1];
            cell.alignment = {
                vertical: 'middle',
                horizontal: numericCols.includes(headerName) ? 'right' : 'center',
            };
            cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'ItemStockMovement.xlsx');
    };

    // ── Render ───────────────────────────────────────────────────
    return (
        <div className="pg-container">

            {/* ── Header ── */}
            <div className="pg-header">
                <h5 className="pg-title">Item Stock Movement</h5>
                <div className="pg-header-actions">

                    {/* Filters row */}
                    <div className="pg-date-row">

                        {/* Item autocomplete */}
                        <div className="pg-field" style={{ minWidth: 220, position: 'relative' }} ref={itemDropRef}>
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>
                                Item <span style={{ color: '#94a3b8', fontWeight: 400 }}>(blank = All)</span>
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                                <input
                                    className="pg-input"
                                    style={{ paddingRight: 24, width: '100%' }}
                                    placeholder="Search by code or name…"
                                    value={itemSearch}
                                    onChange={handleItemInputChange}
                                    onFocus={() => setItemDropOpen(true)}
                                    autoComplete="off"
                                />
                                {(itemSearch || selectedItem) && (
                                    <span
                                        onClick={handleItemClear}
                                        title="Clear"
                                        style={{
                                            position: 'absolute', right: 6, top: '50%',
                                            transform: 'translateY(-50%)',
                                            cursor: 'pointer', color: '#94a3b8',
                                            fontSize: 14, lineHeight: 1,
                                            userSelect: 'none',
                                        }}
                                    >✕</span>
                                )}
                            </div>

                            {/* Dropdown list */}
                            {itemDropOpen && itemSuggestions.length > 0 && (
                                <ul style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0,
                                    background: '#fff', border: '1px solid rgba(200,212,224,0.7)',
                                    borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                                    maxHeight: 220, overflowY: 'auto',
                                    zIndex: 9999, margin: 0, padding: 0,
                                    listStyle: 'none',
                                }}>
                                    <li
                                        onMouseDown={handleItemClear}
                                        style={{
                                            padding: '6px 12px', cursor: 'pointer',
                                            fontSize: 12, color: '#64748b',
                                            borderBottom: '1px solid #f0f4f8',
                                            fontStyle: 'italic',
                                        }}
                                    >— All Items —</li>
                                    {itemSuggestions.map(it => (
                                        <li
                                            key={it.Id}
                                            onMouseDown={() => handleItemSelect(it)}
                                            style={{
                                                padding: '6px 12px', cursor: 'pointer',
                                                fontSize: 12,
                                                background: selectedItem?.Id === it.Id
                                                    ? 'rgba(201,169,110,0.12)' : 'transparent',
                                                borderBottom: '1px solid #f0f4f8',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,169,110,0.10)'}
                                            onMouseLeave={e => e.currentTarget.style.background =
                                                selectedItem?.Id === it.Id ? 'rgba(201,169,110,0.12)' : 'transparent'
                                            }
                                        >
                                            <span style={{ fontWeight: 600, color: '#1c2a3a' }}>{it.Code}</span>
                                            <span style={{ color: '#64748b', marginLeft: 6 }}>{it.Name}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* From Date */}
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input
                                className="pg-input"
                                type="date"
                                value={fromDate}
                                onChange={e => setFromDate(e.target.value)}
                            />
                        </div>

                        {/* To Date */}
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input
                                className="pg-input"
                                type="date"
                                value={toDate}
                                onChange={e => setToDate(e.target.value)}
                            />
                        </div>

                        <button
                            className="pg-btn pg-btn-outline"
                            onClick={fetchTableData}
                            disabled={loading}
                        >
                            <RefreshIcon fontSize="small" /> Refresh
                        </button>
                    </div>

                    {/* Action row */}
                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel} disabled={filteredDataItem.length === 0}>
                            <FeedIcon fontSize="small" /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
                            <SearchIcon fontSize="small" /> Search
                        </button>
                    </div>

                    {showSearch && (
                        <input
                            className="pg-input pg-search-input"
                            placeholder="Search…"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                    )}
                </div>
            </div>

            {/* ── Loading overlay ── */}
            {loading && (
                <div className="pg-loading-overlay">
                    <div className="pg-loading-card">
                        <div className="pg-logo-loader">
                            <div className="pg-logo-block pg-block-green"></div>
                            <div className="pg-logo-block pg-block-blue"></div>
                            <div className="pg-logo-block pg-block-red"></div>
                            <div className="pg-logo-block pg-block-yellow"></div>
                        </div>
                        <p className="pg-loading-text">Loading...</p>
                    </div>
                </div>
            )}

            {/* ── Table ── */}
            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>Transaction Type</th>
                            <th>Document No</th>
                            <th>Reference No</th>
                            <th>Item Code</th>
                            <th>Item Name</th>
                            <th>HSN Code</th>
                            <th style={{ textAlign: 'right' }}>GST Rate</th>
                            <th style={{ textAlign: 'right' }}>Opening Qty</th>
                            <th style={{ textAlign: 'right' }}>In Qty</th>
                            <th style={{ textAlign: 'right' }}>Out Qty</th>
                            <th style={{ textAlign: 'right' }}>Movement Qty</th>
                            <th style={{ textAlign: 'right' }}>Balance Qty</th>
                            <th style={{ textAlign: 'right' }}>Rate</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>GST Amount</th>
                            <th style={{ textAlign: 'right' }}>Total Amount</th>
                            <th>Remarks</th>
                            <th style={{ textAlign: 'right' }}>Curr. Pur. Rate</th>
                            <th style={{ textAlign: 'right' }}>Current MRP</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.length === 0
                            ? (
                                <tr>
                                    <td colSpan={21} style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>
                                        {loading ? '' : 'No records found — select filters and click Refresh'}
                                    </td>
                                </tr>
                            )
                            : filteredDataItem.map((r, i) => (
                                <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.TransactionDate)}</td>
                                    <td>{r.TransactionType}</td>
                                    <td>{r.DocumentNo}</td>
                                    <td>{r.ReferenceNo}</td>
                                    <td>{r.ItemCode}</td>
                                    <td>{r.ItemName}</td>
                                    <td>{r.HSNCode}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt2(r.GSTRate)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt3(r.OpeningQty)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt3(r.InQty)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt3(r.OutQty)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt3(r.MovementQty)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt3(r.BalanceQty)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt2(r.Rate)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt2(r.Amount)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt2(r.GSTAmount)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt2(r.TotalAmount)}</td>
                                    <td>{r.Remarks}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt2(r.CurrentPurchaseRate)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt2(r.CurrentMRP)}</td>
                                </tr>
                            ))
                        }

                        {/* Totals row */}
                        {filteredDataItem.length > 0 && (
                            <tr style={{
                                background: 'linear-gradient(135deg, #1c2a3a 0%, #243447 100%)',
                                color: '#e8f0f8',
                                fontWeight: 700
                            }}>
                                <td colSpan={9} style={{ padding: '6px 10px', textAlign: 'right' }}>Total</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{fmt3(totals.OpeningQty)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{fmt3(totals.InQty)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{fmt3(totals.OutQty)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{fmt3(totals.MovementQty)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{fmt3(totals.BalanceQty)}</td>
                                <td style={{ padding: '6px 10px' }}></td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{fmt2(totals.Amount)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{fmt2(totals.GSTAmount)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{fmt2(totals.TotalAmount)}</td>
                                <td colSpan={3} style={{ padding: '6px 10px' }}></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Record count */}
            {filteredDataItem.length > 0 && (
                <div style={{ padding: '6px 4px', fontSize: 11.5, color: '#64748b' }}>
                    {filteredDataItem.length.toLocaleString()} record{filteredDataItem.length !== 1 ? 's' : ''}
                    {searchQuery ? ` (filtered from ${dataItem.length.toLocaleString()})` : ''}
                </div>
            )}

            <div className="pg-watermark">
                <CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b>
            </div>
        </div>
    );
}

export default ItemStockMovement;
